import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card } from '@/components/ui/card';
import { Brain, Users, TrendingUp, Zap, ArrowRight, CheckCircle2, Target, BarChart3, Lock, Sparkles, Clock, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, Legend } from 'recharts';
import axios from 'axios';
import { AuthContext } from '../App';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const NewLandingPage = () => {
  const navigate = useNavigate();
  const { setUser } = useContext(AuthContext);
  const [showDemo, setShowDemo] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false);
  const [demoData, setDemoData] = useState(null);
  const [allDemos, setAllDemos] = useState(null);
  const [selectedDemoType, setSelectedDemoType] = useState('excellent'); // excellent, moderate, poor
  const [demoTab, setDemoTab] = useState('overview');
  const [password, setPassword] = useState('');
  const [waitlistData, setWaitlistData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    role: ''
  });

  useEffect(() => {
    // Load demo data
    loadDemoData();
    
    // Check for session_id in URL fragment (from Google auth callback)
    const hash = window.location.hash;
    if (hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1].split('&')[0];
      handleSessionId(sessionId);
    }
  }, []);

  const handleSessionId = async (sessionId) => {
    console.log('Received session_id:', sessionId);
    try {
      const response = await axios.post(
        `${API}/auth/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      console.log('Auth response:', response.data);
      setUser(response.data.user);
      window.history.replaceState(null, '', '/dashboard');
      navigate('/dashboard');
    } catch (error) {
      console.error('Session creation error:', error);
      console.error('Error response:', error.response?.data);
      toast.error('Authentication failed. Please try again.');
      window.history.replaceState(null, '', '/');
    }
  };

  const loadDemoData = async () => {
    try {
      const response = await axios.get(`${API}/demo/simulations`);
      setAllDemos(response.data);
      setDemoData(response.data['excellent']); // Default to excellent
    } catch (error) {
      console.error('Failed to load demo data:', error);
    }
  };

  const handleDemoTypeChange = (type) => {
    setSelectedDemoType(type);
    if (allDemos && allDemos[type]) {
      setDemoData(allDemos[type]);
    }
  };

  const handleTryDemo = () => {
    navigate('/demo/excellent');
  };

  const handleJoinWaitlist = () => {
    setShowDemo(false);
    setShowWaitlist(true);
  };

  const submitWaitlist = async () => {
    if (!waitlistData.email || !waitlistData.name) {
      toast.error('Please provide at least your name and email');
      return;
    }

    try {
      await axios.post(`${API}/waitlist`, waitlistData);
      toast.success('Successfully added to waitlist! We\'ll be in touch soon.');
      setShowWaitlist(false);
      setWaitlistData({ name: '', email: '', phone: '', company: '', role: '' });
    } catch (error) {
      toast.error('Failed to join waitlist. Please try again.');
    }
  };

  const handlePlatformAccess = () => {
    setShowPasswordPrompt(true);
  };

  const verifyPassword = async () => {
    try {
      const response = await axios.post(`${API}/auth/check-password`, { password });
      if (response.data.valid) {
        // Redirect to Google auth
        const redirectUrl = `${window.location.origin}/`;
        window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
      } else {
        toast.error('Invalid password');
        setPassword('');
      }
    } catch (error) {
      toast.error('Authentication failed');
    }
  };

  const COLORS = ['#ef4444', '#f97316', '#f59e0b', '#10b981', '#3b82f6'];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50">
      {/* Navigation */}
      <nav className="border-b border-slate-200 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <Brain className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-cyan-600 bg-clip-text text-transparent" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              IntentAI
            </span>
          </div>
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={handleTryDemo}
              className="text-slate-600 hover:text-indigo-600"
            >
              Try Demo
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/pricing')}
              className="text-slate-600 hover:text-indigo-600"
            >
              Pricing
            </Button>
            <Button
              variant="ghost"
              onClick={() => setShowWaitlist(true)}
              className="text-slate-600 hover:text-indigo-600"
            >
              Join Waitlist
            </Button>
            <Button
              onClick={handlePlatformAccess}
              size="sm"
              className="bg-indigo-600 hover:bg-indigo-700 text-white gap-2"
            >
              <Lock className="w-4 h-4" />
              Get Access
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 bg-indigo-50 text-indigo-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Sparkles className="w-4 h-4" />
            Replace expensive market research with AI - Get results in minutes
          </div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 leading-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            <span className="bg-gradient-to-r from-indigo-600 via-purple-600 to-cyan-600 bg-clip-text text-transparent">
              Validate Startup Ideas
            </span>
            <br />
            <span className="text-slate-800">With 90% Accuracy</span>
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto mb-10 leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
            Test your product concepts with thousands of AI-powered consumer simulations. 
            Get detailed purchase intent scores, emotional drivers, and market insights—instantly.
            <br />
            <span className="text-indigo-600 font-semibold">No surveys. No waiting. No expensive panels.</span>
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button
              onClick={handleTryDemo}
              size="lg"
              className="bg-gradient-to-r from-indigo-600 to-cyan-600 hover:from-indigo-700 hover:to-cyan-700 text-white px-10 py-6 text-lg rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Try Interactive Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => setShowWaitlist(true)}
              size="lg"
              variant="outline"
              className="px-10 py-6 text-lg rounded-full font-semibold border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50"
            >
              Join Waitlist
            </Button>
          </div>
        </div>

        {/* Problem/Solution Section */}
        <div className="grid md:grid-cols-2 gap-12 mt-24 mb-24">
          <div className="bg-white rounded-2xl p-8 shadow-lg border border-red-100">
            <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4">
              <DollarSign className="w-6 h-6 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Traditional Market Research
            </h3>
            <ul className="space-y-3 text-slate-600">
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✗</span>
                <span>$10,000 - $50,000+ per study</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✗</span>
                <span>4-8 weeks waiting time</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✗</span>
                <span>Limited sample sizes (50-200 responses)</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✗</span>
                <span>Respondent bias and survey fatigue</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-red-500 mt-1">✗</span>
                <span>Surface-level insights</span>
              </li>
            </ul>
          </div>

          <div className="bg-gradient-to-br from-indigo-50 to-cyan-50 rounded-2xl p-8 shadow-lg border-2 border-indigo-200">
            <div className="w-12 h-12 bg-indigo-600 rounded-lg flex items-center justify-center mb-4">
              <Brain className="w-6 h-6 text-white" />
            </div>
            <h3 className="text-2xl font-bold mb-4 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              IntentAI Platform
            </h3>
            <ul className="space-y-3 text-slate-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="font-medium">10x cheaper - Starting at $49.99 per test</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="font-medium">Results in minutes, not weeks</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="font-medium">Test with 100-1000+ AI consumers</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="font-medium">90% accuracy vs human surveys</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
                <span className="font-medium">Deep demographic & psychographic insights</span>
              </li>
            </ul>
          </div>
        </div>

        {/* How It Works - Detailed */}
        <div className="mt-32 mb-24">
          <h2 className="text-4xl font-bold text-center mb-4 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            How It Works
          </h2>
          <p className="text-center text-slate-600 mb-16 max-w-2xl mx-auto">
            Our platform uses advanced AI models trained on millions of consumer interactions to simulate realistic purchase decisions
          </p>
          <div className="grid md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-500 to-indigo-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Submit Your Concept</h3>
              <p className="text-slate-600">
                Upload product descriptions, ad copy, landing pages, or marketing materials
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>AI Generates Consumers</h3>
              <p className="text-slate-600">
                Create 100-1000 diverse AI consumers with realistic demographics, incomes, and psychographic profiles
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Simulate Reactions</h3>
              <p className="text-slate-600">
                Each AI consumer evaluates your concept, generating authentic responses and purchase intent scores
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-2xl font-bold">
                4
              </div>
              <h3 className="text-xl font-bold mb-3 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Get Deep Insights</h3>
              <p className="text-slate-600">
                Receive detailed analytics: purchase intent scores, emotional drivers, objections, and demographic breakdowns
              </p>
            </div>
          </div>
        </div>

        {/* Features Grid - Enhanced */}
        <div className="mt-32 mb-24">
          <h2 className="text-4xl font-bold text-center mb-16 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Comprehensive Analytics Dashboard
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="p-6 hover:shadow-lg transition-shadow">
              <Target className="w-10 h-10 text-indigo-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Purchase Intent Scores</h3>
              <p className="text-slate-600">
                Get precise 1-5 scale ratings with score distributions showing exactly how consumers feel about your product
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <Users className="w-10 h-10 text-purple-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Demographic Deep-Dives</h3>
              <p className="text-slate-600">
                Analyze intent by age, gender, income, location, and ethnicity. Discover your highest-converting segments
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <TrendingUp className="w-10 h-10 text-green-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Emotional Drivers</h3>
              <p className="text-slate-600">
                Understand what motivates purchase decisions: features, benefits, emotional appeals that resonate most
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <BarChart3 className="w-10 h-10 text-cyan-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Market Segmentation</h3>
              <p className="text-slate-600">
                Identify high, moderate, and low-intent segments with detailed profiles and targeting recommendations
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <Zap className="w-10 h-10 text-yellow-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Objections Analysis</h3>
              <p className="text-slate-600">
                See exactly what concerns consumers have: pricing, skepticism, feature gaps, competitive alternatives
              </p>
            </Card>

            <Card className="p-6 hover:shadow-lg transition-shadow">
              <Clock className="w-10 h-10 text-red-600 mb-4" />
              <h3 className="text-xl font-bold mb-3">Sample Responses</h3>
              <p className="text-slate-600">
                Read authentic, detailed consumer feedback across different intent levels to understand the "why" behind scores
              </p>
            </Card>
          </div>
        </div>

        {/* Use Cases */}
        <div className="mt-32 mb-24 bg-gradient-to-br from-slate-50 to-indigo-50 rounded-3xl p-12">
          <h2 className="text-4xl font-bold text-center mb-12 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Perfect For
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-bold mb-3 text-indigo-600">Product Managers</h3>
              <p className="text-slate-600">
                Validate new features and product concepts before investing in development. Test messaging and positioning.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-bold mb-3 text-purple-600">Marketing Teams</h3>
              <p className="text-slate-600">
                Test ad copy, email campaigns, and landing pages. Optimize messaging for different demographic segments.
              </p>
            </div>
            <div className="bg-white rounded-xl p-6">
              <h3 className="text-xl font-bold mb-3 text-cyan-600">Founders & Entrepreneurs</h3>
              <p className="text-slate-600">
                Get instant market validation for your startup idea without spending thousands on traditional research.
              </p>
            </div>
          </div>
        </div>

        {/* Methodology Section */}
        <div className="mt-32 mb-24">
          <div className="bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl p-10 border border-slate-200">
            <h2 className="text-3xl font-bold text-center mb-6 text-slate-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Our Methodology: Academic Research Meets AI
            </h2>
            <p className="text-center text-slate-600 mb-10 max-w-3xl mx-auto">
              Based on peer-reviewed research: "LLMs Reproduce Human Purchase Intent via Semantic Similarity Elicitation of Likert Ratings"
            </p>
            
            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {/* Current: FLR */}
              <Card className="p-6 bg-white">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">📊</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">Current: FLR Method</h3>
                    <span className="text-sm text-blue-600 font-medium">Follow-up Likert Rating</span>
                  </div>
                </div>
                <p className="text-slate-600 mb-4">
                  Our proprietary two-stage process generates authentic consumer responses, then maps them to purchase intent through a calibrated rating system. Achieves 85% correlation with human data.
                </p>
                <div className="bg-slate-50 rounded-lg p-4 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-slate-800 mb-1">Stage 1: Response Generation</p>
                    <p className="text-sm text-slate-700">
                      Advanced language models simulate diverse consumer personas with realistic demographic and psychographic profiles. Each AI consumer evaluates your product concept naturally, generating free-text responses that mirror human thinking patterns.
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-800 mb-1">Stage 2: Intent Calibration</p>
                    <p className="text-sm text-slate-700">
                      A specialized rating system analyzes response sentiment and language patterns, mapping them to validated 1-5 purchase intent scales. Our proprietary statistical adjustments correct for model biases and ensure accuracy.
                    </p>
                  </div>
                </div>
              </Card>

              {/* Next: SSR */}
              <Card className="p-6 bg-gradient-to-br from-indigo-50 to-purple-50 border-2 border-indigo-200">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center">
                    <span className="text-2xl">🚀</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-800">Next Update: SSR Method</h3>
                    <span className="text-sm text-indigo-600 font-medium">Semantic Similarity Rating</span>
                  </div>
                </div>
                <p className="text-slate-600 mb-4">
                  Our next-generation approach uses neural embedding spaces and semantic distance calculations to create probabilistic purchase intent distributions. Achieves 90% correlation - 5% improvement over FLR.
                </p>
                <div className="bg-white rounded-lg p-4 border border-indigo-200 space-y-3">
                  <div>
                    <p className="text-sm font-semibold text-indigo-800 mb-1">Advanced Semantic Analysis</p>
                    <p className="text-sm text-slate-700">
                      Instead of discrete ratings, SSR maps consumer responses into high-dimensional vector spaces where semantic meaning is preserved. We calculate similarity distances to calibrated anchor statements representing each intent level (1-5).
                    </p>
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-indigo-800 mb-1">Probabilistic Distribution</p>
                    <p className="text-sm text-slate-700">
                      Using proprietary mathematical transformations, similarity scores are converted into probability mass functions across the rating scale. Our statistical enhancements account for response variance and model uncertainty, producing nuanced confidence intervals for each prediction.
                    </p>
                  </div>
                  <div className="border-t border-indigo-100 pt-3">
                    <p className="text-sm text-slate-700 mb-2">
                      <span className="font-semibold text-indigo-700">Key improvements:</span>
                    </p>
                    <ul className="text-sm text-slate-700 space-y-1.5">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span>Captures subtle linguistic nuances missed by binary classification</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span>Produces full probability distributions, not just point estimates</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span>Better aligns with natural human response variability</span>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mt-4 bg-indigo-600 text-white rounded-lg p-3 text-center">
                  <p className="text-sm font-semibold">Coming in Next Version Update</p>
                </div>
              </Card>
            </div>

            <div className="mt-8 text-center">
              <p className="text-sm text-slate-600">
                📄 <a href="https://arxiv.org/html/2510.08338v1" target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-700 underline font-medium">
                  Read the full academic paper
                </a>
              </p>
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="mt-32 bg-gradient-to-r from-indigo-600 to-cyan-600 rounded-3xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Ready to Transform Your Product Research?
          </h2>
          <p className="text-xl mb-8 text-indigo-50 max-w-2xl mx-auto">
            See how IntentAI can help you make data-driven decisions faster and cheaper than traditional market research
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={handleTryDemo}
              size="lg"
              className="bg-white text-indigo-600 hover:bg-indigo-50 px-10 py-6 text-lg rounded-full font-semibold shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Try Interactive Demo
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <Button
              onClick={() => setShowWaitlist(true)}
              size="lg"
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-10 py-6 text-lg rounded-full font-semibold"
            >
              Join Waitlist
            </Button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-slate-200 mt-24 py-8">
        <div className="max-w-7xl mx-auto px-6 text-center text-slate-600">
          <p>&copy; 2025 IntentAI. Powered by advanced AI research in consumer behavior simulation.</p>
          <p className="text-sm mt-2">Based on peer-reviewed methodology with 90% accuracy vs traditional surveys.</p>
        </div>
      </footer>

      {/* Demo Modal */}
      <Dialog open={showDemo} onOpenChange={setShowDemo}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">
              Interactive Demo - Try Different Product Concepts
            </DialogTitle>
            <p className="text-slate-600 mt-2">Select a product type to see how our AI analyzes purchase intent</p>
          </DialogHeader>

          {/* Product Type Selector */}
          <div className="grid grid-cols-3 gap-4 my-4">
            <button
              onClick={() => handleDemoTypeChange('excellent')}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedDemoType === 'excellent'
                  ? 'border-green-500 bg-green-50'
                  : 'border-slate-200 hover:border-green-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className="font-semibold">Excellent Product</span>
              </div>
              <p className="text-sm text-slate-600">High purchase intent (~4.3/5)</p>
            </button>

            <button
              onClick={() => handleDemoTypeChange('moderate')}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedDemoType === 'moderate'
                  ? 'border-yellow-500 bg-yellow-50'
                  : 'border-slate-200 hover:border-yellow-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                <span className="font-semibold">Moderate Product</span>
              </div>
              <p className="text-sm text-slate-600">Mixed intent (~3.0/5)</p>
            </button>

            <button
              onClick={() => handleDemoTypeChange('poor')}
              className={`p-4 rounded-lg border-2 transition-all ${
                selectedDemoType === 'poor'
                  ? 'border-red-500 bg-red-50'
                  : 'border-slate-200 hover:border-red-300'
              }`}
            >
              <div className="flex items-center gap-2 mb-2">
                <div className="w-3 h-3 rounded-full bg-red-500"></div>
                <span className="font-semibold">Poor Product</span>
              </div>
              <p className="text-sm text-slate-600">Low intent (~1.7/5)</p>
            </button>
          </div>

          {/* Product Description */}
          {demoData && (
            <div className="bg-slate-50 p-4 rounded-lg mb-4">
              <h3 className="font-semibold text-lg mb-2">{demoData.product.name}</h3>
              <p className="text-slate-700 text-sm">{demoData.product.description}</p>
            </div>
          )}

          {demoData && (
            <Tabs value={demoTab} onValueChange={setDemoTab} className="mt-6">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="overview">Overview</TabsTrigger>
                <TabsTrigger value="demographics">Demographics</TabsTrigger>
                <TabsTrigger value="insights">Insights</TabsTrigger>
                <TabsTrigger value="responses">Responses</TabsTrigger>
              </TabsList>

              <TabsContent value="overview" className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <Card className="p-6">
                    <h3 className="text-sm font-medium text-slate-600 mb-2">Average Purchase Intent</h3>
                    <p className="text-4xl font-bold text-indigo-600">{demoData.purchase_intent_score.toFixed(2)}</p>
                    <p className="text-sm text-slate-500 mt-1">Out of 5.0</p>
                  </Card>
                  <Card className="p-6">
                    <h3 className="text-sm font-medium text-slate-600 mb-2">Total Simulations</h3>
                    <p className="text-4xl font-bold text-cyan-600">{demoData.num_simulations}</p>
                    <p className="text-sm text-slate-500 mt-1">AI Consumers</p>
                  </Card>
                </div>

                <Card className="p-6">
                  <h3 className="font-semibold mb-4">Score Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={Object.entries(demoData.score_distribution).map(([score, count]) => ({
                      score: `Score ${score}`,
                      count,
                      percentage: ((count / demoData.num_simulations) * 100).toFixed(1)
                    }))}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="score" />
                      <YAxis />
                      <RechartsTooltip 
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            return (
                              <div className="bg-white p-3 border border-slate-200 rounded shadow-lg">
                                <p className="font-semibold">{payload[0].payload.score}</p>
                                <p className="text-indigo-600">Count: {payload[0].value}</p>
                                <p className="text-slate-600">Percentage: {payload[0].payload.percentage}%</p>
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Bar dataKey="count" fill="#6366f1" />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>

                <Card className="p-6">
                  <h3 className="font-semibold mb-4">Market Segments</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(demoData.market_segments).map(([key, segment]) => (
                      <div key={key} className="text-center p-4 bg-slate-50 rounded-lg">
                        <h4 className="font-semibold capitalize mb-2">{key.replace('_', ' ')}</h4>
                        <p className="text-3xl font-bold text-indigo-600">{segment.percentage}%</p>
                      </div>
                    ))}
                  </div>
                </Card>
              </TabsContent>

              <TabsContent value="demographics" className="space-y-6">
                <div className="grid grid-cols-2 gap-6">
                  <Card className="p-6">
                    <h3 className="font-semibold mb-4">Intent by Age Group</h3>
                    <div className="space-y-3">
                      {Object.entries(demoData.demographic_breakdown.by_age).map(([age, data]) => (
                        <div key={age} className="flex justify-between items-center">
                          <span className="text-slate-600">{age}</span>
                          <div className="text-right">
                            <span className="font-semibold text-indigo-600">{data.average_score}</span>
                            <span className="text-sm text-slate-500 ml-2">({data.high_intent_pct}% high)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-semibold mb-4">Intent by Income</h3>
                    <div className="space-y-3">
                      {Object.entries(demoData.demographic_breakdown.by_income).map(([income, data]) => (
                        <div key={income} className="flex justify-between items-center">
                          <span className="text-slate-600">{income}</span>
                          <div className="text-right">
                            <span className="font-semibold text-indigo-600">{data.average_score}</span>
                            <span className="text-sm text-slate-500 ml-2">({data.high_intent_pct}% high)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-semibold mb-4">Intent by Gender</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={Object.entries(demoData.demographic_breakdown.by_gender).map(([gender, data]) => ({
                        gender,
                        score: data.average_score
                      }))} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 5]} />
                        <YAxis dataKey="gender" type="category" />
                        <RechartsTooltip />
                        <Bar dataKey="score" fill="#8b5cf6" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>

                  <Card className="p-6">
                    <h3 className="font-semibold mb-4">Intent by Location</h3>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={Object.entries(demoData.demographic_breakdown.by_location).map(([location, data]) => ({
                        location,
                        score: data.average_score
                      }))} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis type="number" domain={[0, 5]} />
                        <YAxis dataKey="location" type="category" />
                        <RechartsTooltip />
                        <Bar dataKey="score" fill="#06b6d4" />
                      </BarChart>
                    </ResponsiveContainer>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="insights" className="space-y-6">
                <Card className="p-6">
                  <h3 className="font-semibold mb-4 text-green-600">Key Emotional Drivers</h3>
                  <ul className="space-y-2">
                    {demoData.emotional_drivers.map((driver, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{driver}</span>
                      </li>
                    ))}
                  </ul>
                </Card>

                <Card className="p-6">
                  <h3 className="font-semibold mb-4 text-red-600">Top Objections & Concerns</h3>
                  <ul className="space-y-2">
                    {demoData.objections.map((objection, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="text-red-500 mt-1 flex-shrink-0">⚠</span>
                        <span className="text-slate-700">{objection}</span>
                      </li>
                    ))}
                  </ul>
                </Card>

                <Card className="p-6">
                  <h3 className="font-semibold mb-4">Messaging Recommendations</h3>
                  <ul className="space-y-2">
                    {demoData.demographic_insights.messaging_recommendations.map((rec, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <Sparkles className="w-5 h-5 text-indigo-600 mt-0.5 flex-shrink-0" />
                        <span className="text-slate-700">{rec}</span>
                      </li>
                    ))}
                  </ul>
                </Card>
              </TabsContent>

              <TabsContent value="responses" className="space-y-4">
                {demoData.sample_responses.map((response, idx) => (
                  <Card key={idx} className="p-6">
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${
                          response.category === 'High Intent' ? 'bg-green-100 text-green-700' :
                          response.category === 'Moderate Intent' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {response.category}
                        </span>
                        <span className="ml-3 text-sm text-slate-600">{response.demographic}</span>
                      </div>
                      <div className="text-right">
                        <span className="text-2xl font-bold text-indigo-600">{response.score.toFixed(2)}</span>
                        <span className="text-sm text-slate-500 ml-1">/ 5.0</span>
                      </div>
                    </div>
                    <p className="text-slate-700 leading-relaxed">{response.response}</p>
                  </Card>
                ))}
              </TabsContent>
            </Tabs>
          )}

          <div className="mt-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
            <p className="text-center text-slate-700 mb-4">
              <strong>Like what you see?</strong> Get these insights for your own products in minutes.
            </p>
            <div className="flex gap-4 justify-center">
              <Button onClick={handleJoinWaitlist} size="lg" className="bg-indigo-600 hover:bg-indigo-700">
                Join Waitlist
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Waitlist Modal */}
      <Dialog open={showWaitlist} onOpenChange={setShowWaitlist}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold">Join the Waitlist</DialogTitle>
            <p className="text-slate-600 mt-2">Be among the first to access IntentAI and transform your product research</p>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
              <Input
                value={waitlistData.name}
                onChange={(e) => setWaitlistData({ ...waitlistData, name: e.target.value })}
                placeholder="Your full name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
              <Input
                type="email"
                value={waitlistData.email}
                onChange={(e) => setWaitlistData({ ...waitlistData, email: e.target.value })}
                placeholder="your.email@company.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Phone</label>
              <Input
                type="tel"
                value={waitlistData.phone}
                onChange={(e) => setWaitlistData({ ...waitlistData, phone: e.target.value })}
                placeholder="+1 (555) 123-4567"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Company</label>
              <Input
                value={waitlistData.company}
                onChange={(e) => setWaitlistData({ ...waitlistData, company: e.target.value })}
                placeholder="Your company name"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Role</label>
              <Input
                value={waitlistData.role}
                onChange={(e) => setWaitlistData({ ...waitlistData, role: e.target.value })}
                placeholder="Product Manager, Founder, etc."
              />
            </div>

            <Button onClick={submitWaitlist} className="w-full bg-indigo-600 hover:bg-indigo-700" size="lg">
              Join Waitlist
              <ArrowRight className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Access Options Modal */}
      <Dialog open={showPasswordPrompt} onOpenChange={setShowPasswordPrompt}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold flex items-center gap-2">
              <Lock className="w-6 h-6 text-indigo-600" />
              Get Platform Access
            </DialogTitle>
            <p className="text-slate-600 mt-2">Choose how you'd like to access IntentAI</p>
          </DialogHeader>

          <div className="space-y-4 mt-6">
            {/* Paid Access Option */}
            <div className="border-2 border-indigo-200 rounded-lg p-6 bg-indigo-50">
              <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                <span className="text-indigo-600">💳</span>
                Purchase Access
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Get instant access with our flexible pricing plans
              </p>
              <Button 
                onClick={() => {
                  setShowPasswordPrompt(false);
                  navigate('/pricing');
                }}
                className="w-full bg-indigo-600 hover:bg-indigo-700"
              >
                View Pricing Options
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-slate-200"></div>
              <span className="text-sm text-slate-500">OR</span>
              <div className="flex-1 h-px bg-slate-200"></div>
            </div>

            {/* Invite Code Option */}
            <div className="border-2 border-slate-200 rounded-lg p-6">
              <h3 className="font-semibold text-lg mb-2 flex items-center gap-2">
                <span className="text-slate-600">🎟️</span>
                Have an Invite Code?
              </h3>
              <p className="text-sm text-slate-600 mb-4">
                Enter your invite code for free access
              </p>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter invite code"
                className="mb-3"
                onKeyPress={(e) => e.key === 'Enter' && verifyPassword()}
              />
              <Button 
                onClick={verifyPassword} 
                className="w-full bg-slate-900 hover:bg-slate-800"
                variant="outline"
              >
                Verify Code
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NewLandingPage;
